/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useQuery} from 'react-query';
import {userPermission} from '@ion/core/src/services/Connect';
import {Permission} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

type WrappedPermission = {permission: Permission};

type WrappedPermissions = WrappedPermission[];

export type useUserPermissionsResult = UseDefaultQueryResult & {
  userPermissions: WrappedPermissions;
};

/**
 * The useUserPermissions hook provides the Query for referencing the permissions.
 *
 * ```typescript
 *
 * import {useUserPermissions} from '@ion/query/src/hooks/';
 *
 *  ...
 *
 *  const {pegLabel} = useUserPermissions();
 *
 * ```
 *
 * import {useUserPermissions} from '@ion/query/src/hooks/';
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @returns useUserPermissionsResult containing the query results and the permissions.
 */
export const useUserPermissions = (enabled = true, staleTime = getStaleTime(5, false, false)
): useUserPermissionsResult => {
  const query = useQuery(
    [QueryKey.GET_PERMISSIONS],
    userPermission,
    {staleTime: staleTime as number, enabled}
  );
  return createQueryResult(query, {
    userPermissions : query.data || []
  }) as unknown as useUserPermissionsResult;
};

export default useUserPermissions;
